import { action, observable, runInAction, toJS } from 'mobx';

import BaseStore from 'bz-stores/mobx/base-store';

import createContext from 'bz-utilities/hooks/context-v2';

import Api from 'tm3-core/api';
import { CustomLookupRequest } from 'tm3-core/api.dtos';
import type { CustomLookupResponse } from 'tm3-core/api.dtos';

import type { ClinicalListStore } from './clinical-lists-store';

export const [CustomListStoreContext, useCustomListStore] =
  createContext<CustomListStore>('CustomListStore');

class CustomListStore extends BaseStore<unknown> {
  @observable id: null | number = null;
  @observable name = '';
  @observable alternates = observable.array<string>([]);
  @observable value = observable.map<string, any>(new Map());

  groupId: null | number = null;
  serviceTypeId: null | number = null;
  globalId: null | string = null;

  store?: null | ClinicalListStore = null;

  constructor(props: CustomLookupResponse, store?: ClinicalListStore) {
    super();

    this.onInitialise(props, store);
  }

  @action.bound
  onInitialise(props: CustomLookupResponse, store?: ClinicalListStore): void {
    let { id, name, value, alternates, groupId, serviceTypeId, globalId } = props;

    this.id = id;
    this.name = name;
    this.alternates.replace(alternates);
    this.groupId = groupId;
    this.serviceTypeId = serviceTypeId;
    this.globalId = globalId;

    const obj = JSON.parse(value);

    this.value.replace(obj);

    this.store = store;
  }

  @action.bound
  onApply(value: object) {
    let { id, name, alternates, groupId, serviceTypeId } = this;

    return new Promise((resolve, reject) => {
      let request = {
        id,
        name,
        alternates,
        value: JSON.stringify(value),
        groupId,
        serviceTypeId
      };

      Api.put(`/api/lookups/nested/${id}`, request).then((res) => {
        if (!!this.store) this.store.promise = null;

        if (Object.keys(value).length > 0) this.value.replace(value);

        resolve(true);
      });
    });
  }

  @action.bound
  onSubmit() {
    let { id, name, value, alternates, serviceTypeId } = this;
    return new Promise((resolve, reject) => {
      let request = {
        id,
        name,
        alternates,
        value: JSON.stringify(toJS(value)),
        serviceTypeId
      };

      Api.put(`/api/lookups/nested/${id}`, request).then((res) => {
        resolve(true);
      });
    });
  }

  @action.bound
  async onUpdate(key: string | number, menu?: any): Promise<boolean> {
    let { id, name, value, alternates, serviceTypeId } = this;
    let lookup = toJS(value);

    if (!menu) {
      lookup.delete(key);
    } else {
      lookup.set(key, menu);
    }

    let request = new CustomLookupRequest({
      id: id!,
      name,
      alternates,
      value: JSON.stringify(lookup),
      serviceTypeId: serviceTypeId!
    });

    await this.client.put(request);

    runInAction(() => {
      if (!menu) {
        this.value.delete(key);
      } else {
        this.value.set(key, menu);
      }
    });

    return !menu;
  }

  @action.bound
  async onRemove() {
    await this.client.delete(new CustomLookupRequest({ id: this.id! }));
  }
}

export default CustomListStore;
